import axios from "axios";
import { useState, useEffect } from "react";
import { useHistory } from "react-router";
import SignedInNavbar from "../../components/navbar/signed-in-navbar";
import { BACKEND_URL } from "../../constants/Strings";
import AdminProtectedRoute from "./adminProtectedRotue";

function AdminAllFilms() {
  const navigate = useHistory();
  const [allFilms, setAllFilms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [filteredFilms, setFilteredFilms] = useState(undefined);
  const [filmId, setFilmId] = useState(undefined);

  function getDateDisplayString(date) {
    if (!date || date.length === 0) return "";
    return new Date(date).toDateString();
  }

  // derive a readable status string from the JSON
  function getStatusLabel(film) {
    // if backend already sends 'status', prefer that
    if (film?.status) return film.status;

    switch (film?.status_id) {
      case 1:
        return "Pending";
      case 2:
        return "Approved";
      case 3:
        return "Rejected";
      default:
        return "Unknown";
    }
  }

  const getStatusSlug = (film) => getStatusLabel(film).toLowerCase();

  // Get film data
  const getFilmsFromLaravel = async () => {
    setIsLoading(true);

    try {
      const response = await axios.get(`${BACKEND_URL}api/all-films`);
      // New JSON: response is an array of films
      if(response.data?.films && Array.isArray(response.data?.films) )
        setAllFilms(response.data.films);
      else if(response.data && Array.isArray(response.data) )
        setAllFilms(response.data);
      else
        setAllFilms([]);
      console.log("All Films Data", response.data);
    } catch (e) {
      console.log(e);
      displayMessage(false, "Failed to load films!");
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  useEffect(() => {
    document.querySelector("body").style.paddingTop = "0px";
    document.querySelector("body").style.margin = "0 !important";
    document.querySelector("#root").style.overflow = "hidden";
    getFilmsFromLaravel();
  }, []);

  // Apply filters whenever data or filters change
  useEffect(() => {
    const normalizedName = nameFilter.toLowerCase();

    if (statusFilter === "all" && normalizedName.length === 0) {
      // No filters
      setFilteredFilms(allFilms);
    } else if (statusFilter === "all" && normalizedName.length > 0) {
      // Only name filter
      setFilteredFilms(
        allFilms?.filter((film) =>
          film?.title?.toLowerCase()?.includes(normalizedName)
        )
      );
    } else if (statusFilter !== "all" && normalizedName.length === 0) {
      // Only status filter
      setFilteredFilms(
        allFilms?.filter((film) =>
          getStatusSlug(film)?.includes(statusFilter)
        )
      );
    } else {
      // Both filters
      setFilteredFilms(
        allFilms?.filter(
          (film) =>
            getStatusSlug(film)?.includes(statusFilter) &&
            film?.title?.toLowerCase()?.includes(normalizedName)
        )
      );
    }
  }, [allFilms, statusFilter, nameFilter]);

  function goToForm(id, link) {
    console.log(id);
    navigate.push({
      pathname: "/admin/upload/films",
      state: [{ filmId: id, externalLink: link }],
    });
  }

  async function approveVideo(id) {
    console.log(id);
    setIsLoading(true);

    try {
      const response = await axios.get(`${BACKEND_URL}api/film/approve/${id}`);

      if (response.status === 200) {
        console.log("Successfully");
        displayMessage(true, "Film was successfully approved!");
        getFilmsFromLaravel();
      } else {
        displayMessage(false, "Failed to approve film!");
      }
    } catch (e) {
      console.log(e);
      displayMessage(false, "Failed to approve film!");
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }

  async function rejectVideo(id) {
    console.log(id);
    setIsLoading(true);

    try {
      const response = await axios.get(`${BACKEND_URL}api/film/reject/${id}`);

      if (response.status === 200) {
        console.log("Successfully");
        displayMessage(true, "Film was successfully rejected!");
        getFilmsFromLaravel();
      } else {
        displayMessage(false, "Failed to reject film!");
      }
    } catch (e) {
      console.log(e);
      displayMessage(false, "Failed to reject film!");
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }

  function hoveringFilm(id) {
    setFilmId(id);
  }

  function displayMessage(isSuccess, message) {
    const successIcon = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        fill="currentColor"
        className="bi bi-check-circle-fill flex-shrink-0 me-2"
        viewBox="0 0 16 16"
        role="img"
        aria-label="Success:"
      >
        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
      </svg>
    );

    const errorIcon = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        fill="currentColor"
        className="bi bi-exclamation-triangle-fill flex-shrink-0 me-2"
        viewBox="0 0 16 16"
        role="img"
        aria-label="Warning:"
      >
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
      </svg>
    );

    setNotification(
      <div
        className={`alert ${
          isSuccess ? "alert-success" : "alert-danger"
        } alert-dismissible fade show`}
        role="alert"
      >
        {isSuccess ? successIcon : errorIcon}
        <strong>{message}</strong>
      </div>
    );

    setTimeout(() => {
      setNotification(undefined);
    }, 5000);
  }

  let context = <p className="text-center">No Films were found!</p>;

  if (filteredFilms && filteredFilms.length > 0) {
    context = (
      <table className="table table-light table-borderless">
        <thead className="thead-dark">
          <tr>
            <th>Filmmaker ID</th>
            <th scope="col">Title</th>
            <th>Status</th>
            <th>Film Type</th>
            <th>Genre</th>
            <th>Description</th>
            <th>Runtime</th>
            <th>Promo Code</th>
            <th>Created Date</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {filteredFilms?.map((film) => {
            const statusLabel = getStatusLabel(film);
            const statusSlug = statusLabel.toLowerCase();

            return (
              <tr
                key={film.uuid}
                className={`${filmId === film?.id ? "table-active" : ""} 
                  ${
                    statusSlug.includes("approve")
                      ? "table-success"
                      : statusSlug.includes("rejected")
                      ? "table-danger"
                      : ""
                  }`}
                onMouseOver={() => hoveringFilm(film?.id)}
                onMouseOut={() => hoveringFilm(undefined)}
              >
                <td>{film?.filmaker_id}</td>

                <td>
                  {film?.film_file == null ? (
                    film?.title
                  ) : (
                    <a
                      href={`/admin/screening-room?id=${film?.id}`}
                      target={"_blank"}
                      rel={"noreferrer"}
                    >
                      {film?.title}
                    </a>
                  )}
                </td>

                <th scope="row">{statusLabel}</th>

                <td>{film?.film_type}</td>
                <td>{film?.genre}</td>
                <td>{film?.description}</td>
                <td>{film?.runtime}</td>
                <td>{film?.promo_code}</td>
                <td>{getDateDisplayString(film?.created_at)}</td>

                <td className="d-grid gap-2">
                  <>
                    {!statusSlug.includes("approve") && (
                      <button
                        type="button"
                        onClick={() => approveVideo(film?.id)}
                        className="btn btn-success btn-sm"
                      >
                        <i
                          className="fa fa-upload"
                          style={{ fontSize: "24px" }}
                        ></i>{" "}
                        Approve
                      </button>
                    )}

                    {!statusSlug.includes("reject") && (
                      <button
                        type="button"
                        onClick={() => rejectVideo(film?.id)}
                        className="btn btn-danger btn-sm"
                      >
                          <i
                          className="fa fa-upload"
                          style={{ fontSize: "24px" }}
                        ></i>{" "}
                        Reject
                      </button>
                    )}
                  </>

                  {film?.film_file == null && (
                    <button
                      type="button"
                      onClick={() =>
                        goToForm(film?.id, film?.external_link)
                      }
                      className="btn btn-primary btn-sm"
                    >
                      <i
                        className="fa fa-upload"
                        style={{ fontSize: "24px" }}
                      ></i>{" "}
                      Upload Video
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <AdminProtectedRoute>
      <div>
        <SignedInNavbar />
      </div>

      <div
        className="justify-center align-center"
        style={{ overflow: "hidden", maxHeight: "100vh", margin: "8vh" }}
      >
        <div className="heading d-flex flex-row justify-content-between">
          <h1>All Films</h1>

          <div className="filters_wrapper d-flex flex-row gap-2">
            {/* Search Container */}
            <div className="mw-10 ml-2">
              <div className="input-group mr-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Movie Name"
                  aria-label="Movie Name"
                  onChange={(e) => setNameFilter(e.target.value)}
                />
              </div>
            </div>

            {/* Select container */}
            <div className="mw-10">
              <select
                className="form-select form-select-sm"
                defaultValue={"all"}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value={"all"}>All</option>
                <option value={"pending"}>Pending Approval</option>
                <option value={"approved"}>Approved</option>
                <option value={"rejected"}>Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {notification ? notification : <></>}

        <div className="col w-auto justify-content-center table-responsive">
          {isLoading && (
            <p className="text-center mt-2">Loading films, please wait...</p>
          )}
          {context}
        </div>
      </div>
    </AdminProtectedRoute>
  );
}

export default AdminAllFilms;
